import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import moment from 'moment-timezone';

import { TimeService } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { DATE_FORMAT, getDefaultTimezone } from '@recruitee/time';

import { ButtonComponent } from '../../common/button/components/button/button.component';
import { GroupComponent } from '../../common/group/group.component';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { segmentedButtonType } from '../../common/segmented-control/segmented-control-button/segmented-control-button.component';
import { SegmentedControlComponent } from '../../common/segmented-control/segmented-control.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import { RtInput } from '../../utils/rt-input';
import { DatePickerComponent, DatePickerDirection } from '../date-picker/date-picker.component';
import { InputComponent } from '../input/input.component';
import { TimeZonePickerComponent } from '../time-zone-picker/time-zone-picker.component';
import { TimePickerDropdownComponent } from './time-picker-dropdown/time-picker-dropdown.component';

export type TimeData = {
  date: string; // 'YYYY-MM-DD'
  time: string; // 'HH:mm'
  zone: string; // 'Europe/Warsaw'
};

@Component({
  selector: 'rt-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(focus)': 'focus.emit($event)',
    '(blur)': 'blur.emit($event)',
  },
  standalone: true,
  imports: [
    ButtonComponent,
    ConnectedOverlayDirective,
    DatePickerComponent,
    GroupComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    SegmentedControlComponent,
    TimePickerDropdownComponent,
    TimeZonePickerComponent,
    TranslatePipe,
  ],
})
export class TimePickerComponent extends RtInput<TimeData | null> implements OnChanges {
  @Input() public defaultDate: string = moment().format(DATE_FORMAT);
  @Input() public defaultTime: string = '12:00';
  // f.e. "Europe/Warsaw"
  @Input() public defaultTimeZoneName: string = getDefaultTimezone();
  @Input() public showDoneButton: boolean = true;
  @Input() public blockPastTime: boolean = false;
  @Input() public direction: DatePickerDirection = 'any';

  @Input() public timeInterval: number = 0;

  @Output() public isTimeValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  public activeTab: 'date' | 'time' = 'date';
  public showTimeZonePicker: boolean = false;
  public isPresentDaySelected: boolean = false;
  public tmpValue: TimeData | null;

  public segmentedButtons: Array<segmentedButtonType<'date' | 'time'>>;

  constructor(
    private i18n: I18nService,
    private timeService: TimeService,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
    this.setTmpValue(this.value, false);
    this.setSegmentedButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setTmpValue({
      date: this.defaultDate,
      time: this.defaultTime,
      zone: this.defaultTimeZoneName,
    });

    if (changes.defaultDate || changes.defaultTime) {
      this.setIsPresentDaySelected(this.defaultDate);
      this.validatePastTime(this.defaultTime);
    }
  }

  public handleDateChange(date: string | string[]): void {
    if (date) {
      this.tmpValue = <TimeData>{
        ...this.tmpValue,
        date,
      };
      this.setIsPresentDaySelected(date);
      this.validatePastTime(this.tmpValue.time);
      this.cdRef.detectChanges();
    }
  }

  public handleTimeChange(time: string): void {
    this.validatePastTime(time);
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      time,
    };
    this.cdRef.detectChanges();
  }

  public handleTimeZoneChange(zone: string): void {
    this.tmpValue = <TimeData>{
      ...this.tmpValue,
      zone,
    };
    this.validatePastTime(this.tmpValue.time);
    this.cdRef.detectChanges();
  }

  public applyChanges(): void {
    this.handleChange(this.tmpValue);
    this.cdRef.detectChanges();
  }

  public switchTab(tab: 'date' | 'time'): void {
    this.activeTab = tab;
    this.setSegmentedButtons();
    this.cdRef.detectChanges();
  }

  protected setValue(value: TimeData | null): void {
    this.value = value;
    this.setTmpValue(value);
  }

  private validatePastTime(time: string): void {
    if (this.blockPastTime) {
      const isTimeValid =
        !this.isPresentDaySelected ||
        (this.timeService.isDateValid(time, 'LT') && !this.timeService.isBeforeNow(time, 'LT'));

      this.isTimeValid.emit(isTimeValid);
    }
  }

  private setIsPresentDaySelected(date: string | string[]): void {
    if (!Array.isArray(date) && this.blockPastTime) {
      this.isPresentDaySelected = this.timeService.isToday(date);
    }
  }

  private setTmpValue(value: TimeData | null, runDetectChanges: boolean = false): void {
    const time = (value && value.time) || this.defaultTime;
    this.tmpValue = {
      date: (value && value.date) || this.defaultDate,
      time: moment(time, 'HH:mm').format('LT'),
      zone: (value && value.zone) || this.defaultTimeZoneName,
    };
    if (runDetectChanges) {
      this.cdRef.detectChanges();
    }
  }

  private setSegmentedButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.activeTab === 'date',
        value: 'date',
        label: this.i18n.translate('rtu.common.date'),
        isAvailable: true,
      },
      {
        isActive: this.activeTab === 'time',
        value: 'time',
        label: this.i18n.translate('rtu.common.time'),
        isAvailable: true,
      },
    ];
  }
}
